import {useEffect, useMemo, useState} from 'react';
import {ehrService} from '../../services/ehrService';

// ── useTodayPatients ──────────────────────────────────────────────
//
// Hydrates the EHR patient docs referenced by today's appointment
// `statusByPatientId` map (from `useAppointmentStatuses`). Used by the
// new EHR-only "Today's appointments" picker section so that every
// patient with a relevant appointment today renders correctly,
// regardless of whether they happened to land in the (legacy) 200-doc
// `fetchCachedPatients` slab.
//
// Returns:
//   {
//     todayPatients: Array<unifiedItem>  — buildUnifiedPatientList shape,
//                                          with `matchKind: 'todayOnly'`,
//                                          ready to slot into existing
//                                          row-rendering paths.
//     loading: boolean
//   }
//
// The patient docs are fetched in chunks via
// `ehrService.fetchCachedPatientsByIds`, which already handles document-id
// + `ehr_source_id` fallbacks for legacy mismatch shapes.
export default function useTodayPatients({
  companyId,
  hasActiveEhr,
  statusByPatientId,
}: Record<string, any>) {
  const [hydratedById, setHydratedById] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);

  const idsKey = useMemo(() => {
    if (!statusByPatientId || !statusByPatientId.size) return '';
    return [...statusByPatientId.keys()].filter(Boolean).sort().join(',');
  }, [statusByPatientId]);

  useEffect(() => {
    if (!hasActiveEhr || !companyId || !idsKey) {
      setHydratedById({});
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const ids = idsKey.split(',').filter(Boolean);

    ehrService
      .fetchCachedPatientsByIds(companyId, ids)
      .then(map => {
        if (!cancelled) setHydratedById(map || {});
      })
      .catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('useTodayPatients hydrate failed:', err?.message);
        }
        if (!cancelled) setHydratedById({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, hasActiveEhr, idsKey]);

  const todayPatients = useMemo(() => {
    if (!statusByPatientId || !statusByPatientId.size) return [];
    const items = [];
    for (const id of statusByPatientId.keys()) {
      if (!id) continue;
      const ehrPatient = hydratedById[id];
      if (!ehrPatient) continue;
      const firstName = ehrPatient.firstName || '';
      const lastName = ehrPatient.lastName || '';
      items.push({
        id: `today_${id}`,
        localPatient: null,
        ehrPatient,
        matchKind: 'todayOnly',
        displayName: lastName
          ? `${lastName}, ${firstName}`
          : firstName || 'Unknown',
        sortName: (lastName || firstName || '').toLowerCase(),
      });
    }
    return items;
  }, [hydratedById, statusByPatientId]);

  return {todayPatients, loading};
}
